/**
 * Conexion con la `GameRoom`, **fuera** del ciclo de vida de React.
 *
 * Una conexion es un recurso de la pagina, no de un componente. Tenerla dentro
 * de un `useEffect` fue el origen de dos fallos reales:
 *
 * - En desarrollo `StrictMode` monta, desmonta y remonta, de modo que el efecto
 *   corria dos veces. Cada montaje abria su propia conexion, el servidor veia
 *   **dos jugadores** en la misma sala y arrancaba la partida contra un
 *   fantasma de la propia pestaña.
 * - Cualquier intento de evitarlo con una guarda dejaba el caso contrario: si el
 *   primer montaje se descartaba a si mismo, el segundo no reconectaba y la
 *   pestaña se quedaba sin conexion para siempre.
 *
 * Aqui la conexion es **unica por pagina** e idempotente: montar dos veces no
 * abre dos sockets. Reconectar cierra siempre la anterior **esperando** a que
 * termine, de modo que nunca quedan dos conexiones vivas contra la misma sala.
 */

import { Client } from '@colyseus/sdk';

import { parseClientEnv } from '../config';
import {
  ACTION_MESSAGE,
  REJECTED_MESSAGE,
  SNAPSHOT_MESSAGE,
  parseRejection,
  parseSnapshot,
  type ActionRejection,
} from './snapshot';
import type { MatchView, PlayerAction } from '@tcg/game-rules';

/** Nombre con el que el servidor registra la sala. */
export const GAME_ROOM_NAME = 'game';

export type ConnectionPhase = 'idle' | 'connecting' | 'waiting' | 'playing' | 'error';

export interface GameRoomState {
  readonly phase: ConnectionPhase;
  readonly roomId: string | null;
  /** Ultimo snapshot. `null` mientras la partida no haya empezado. */
  readonly view: MatchView | null;
  readonly rejection: ActionRejection | null;
  readonly error: string | null;
  /** A donde se intento conectar, util para diagnosticar un fallo de red. */
  readonly endpoint: string | null;
  /** Historial corto de eventos de interfaz, no de dominio. */
  readonly log: readonly string[];
}

/**
 * Lo minimo que este modulo necesita de una sala.
 *
 * Se declara aqui en lugar de usar el tipo del SDK para poder inyectar un doble
 * en los tests sin levantar un servidor.
 */
export interface GameRoomLike {
  readonly roomId: string;
  onMessage(type: string, handler: (payload: unknown) => void): unknown;
  onLeave(handler: () => void): unknown;
  send(type: string, payload: unknown): void;
  leave(): Promise<unknown>;
}

export interface GameClientLike {
  joinOrCreate(roomName: string): Promise<GameRoomLike>;
}

export interface ConnectionDeps {
  /** Crea el cliente. Se llama una vez por intento de conexion. */
  readonly createClient: (endpoint: string) => GameClientLike;
  readonly endpoint: string;
}

const MAX_LOG = 20;

const INITIAL: GameRoomState = {
  phase: 'idle',
  roomId: null,
  view: null,
  rejection: null,
  error: null,
  endpoint: null,
  log: [],
};

export interface GameConnection {
  getState: () => GameRoomState;
  subscribe: (listener: () => void) => () => void;
  /** Conecta si no hay ya una conexion o un intento en curso. Idempotente. */
  ensureConnected: () => Promise<void>;
  /** Cierra la conexion actual **esperando** a que termine, y vuelve a conectar. */
  retry: () => Promise<void>;
  /** Cierra la conexion actual, esperando a que termine. */
  disconnect: () => Promise<void>;
  send: (action: PlayerAction) => void;
}

/**
 * Crea un gestor de conexion.
 *
 * Se exporta para poder probarlo con un cliente falso; la aplicacion usa la
 * instancia unica {@link gameConnection}.
 */
export function createGameConnection(deps: ConnectionDeps): GameConnection {
  let state: GameRoomState = INITIAL;
  const listeners = new Set<() => void>();

  /**
   * Intento en curso o establecido.
   *
   * Se guarda la **promesa** y no solo la sala: es lo que permite cerrar una
   * conexion cuyo `joinOrCreate` todavia no ha resuelto, en lugar de dejarla
   * huerfana.
   */
  let attempt: { readonly pending: Promise<GameRoomLike>; room: GameRoomLike | null } | null = null;

  function emit(next: GameRoomState): void {
    state = next;
    for (const listener of listeners) {
      listener();
    }
  }

  function patch(change: Partial<GameRoomState>, entry?: string): void {
    emit({
      ...state,
      ...change,
      log: entry === undefined ? state.log : [...state.log, entry].slice(-MAX_LOG),
    });
  }

  function bind(room: GameRoomLike): void {
    room.onMessage(SNAPSHOT_MESSAGE, (payload: unknown) => {
      const view = parseSnapshot(payload);

      if (view === null) {
        patch(
          { error: 'Se recibio un snapshot con forma inesperada.' },
          'Snapshot descartado: forma inesperada',
        );
        return;
      }

      // Un snapshot nuevo significa que algo si se aplico: el rechazo anterior
      // deja de ser el ultimo evento relevante.
      patch(
        { phase: 'playing', view, rejection: null },
        `Snapshot: turno ${String(view.turn.turnNumber)}`,
      );
    });

    room.onMessage(REJECTED_MESSAGE, (payload: unknown) => {
      const rejection = parseRejection(payload);

      if (rejection !== null) {
        patch({ rejection }, `Rechazada: ${rejection.code}`);
      }
    });

    room.onLeave(() => {
      // Solo interesa si sigue siendo la conexion activa: al reconectar se
      // cierra la anterior a proposito y ese cierre no es un error.
      if (attempt?.room === room) {
        attempt = null;
        patch(
          { phase: 'error', error: 'La conexion con la sala se cerro. Pulsa «Reintentar».' },
          'Conexion cerrada',
        );
      }
    });
  }

  async function connect(): Promise<void> {
    const client = deps.createClient(deps.endpoint);
    const pending = client.joinOrCreate(GAME_ROOM_NAME);
    const started = { pending, room: null as GameRoomLike | null };

    attempt = started;
    patch({ phase: 'connecting', endpoint: deps.endpoint, error: null }, 'Conectando…');

    try {
      const room = await pending;

      if (attempt !== started) {
        // Alguien reconecto mientras tanto: esta conexion ya no manda y se
        // cierra en lugar de quedarse abierta ocupando un asiento.
        await room.leave();
        return;
      }

      started.room = room;
      bind(room);
      patch(
        { phase: 'waiting', roomId: room.roomId, error: null },
        `Unido a la sala ${room.roomId}`,
      );
    } catch (cause: unknown) {
      if (attempt !== started) {
        return;
      }

      attempt = null;

      const detail = cause instanceof Error ? cause.message : String(cause);

      patch(
        {
          phase: 'error',
          endpoint: deps.endpoint,
          error: `No se pudo entrar en la sala en ${deps.endpoint}: ${detail}`,
        },
        `Fallo al unirse: ${detail}`,
      );
    }
  }

  async function disconnect(): Promise<void> {
    const active = attempt;

    attempt = null;

    if (active === null) {
      return;
    }

    try {
      // Se espera al `joinOrCreate` antes de cerrar: si se cerrara sin esperar,
      // una conexion aun en vuelo quedaria abierta para siempre.
      const room = active.room ?? (await active.pending);

      await room.leave();
    } catch {
      // Si el join fallo no hay nada que cerrar.
    }
  }

  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    ensureConnected: async () => {
      if (attempt !== null) {
        return;
      }

      await connect();
    },
    retry: async () => {
      patch({ ...INITIAL, log: state.log }, 'Reintentando…');
      await disconnect();
      await connect();
    },
    disconnect,
    send: (action) => {
      const room = attempt?.room;

      if (room === undefined || room === null) {
        return;
      }

      room.send(ACTION_MESSAGE, action);
      patch({}, `Enviada: ${action.type}`);
    },
  };
}

/** Conexion unica de la pagina. */
export const gameConnection: GameConnection = createGameConnection({
  endpoint: parseClientEnv().VITE_SERVER_URL,
  createClient: (endpoint) => new Client(endpoint) as unknown as GameClientLike,
});
